import React, { useEffect, useRef, useState } from 'react';
import AdminBackLink from '../components/AdminBackLink';

import axiosInstance from '../services/api';
import api from '../config/api';
import { ArticleDraft, ArticleDraftListItem } from '../types';
import { HiOutlineEye, HiOutlineTrash } from 'react-icons/hi';
import RichTextEditor, { RichTextEditorRef } from '../components/RichTextEditor';
import './NewsJudgments.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getImageUrl = (imagePath?: string | null) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${API_URL.replace('/api', '')}${imagePath}`;
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getPaginationItems = (current: number, count: number): Array<number | 'ellipsis'> => {
  if (count <= 7) return Array.from({ length: count }, (_, index) => index + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, 'ellipsis', count];
  if (current >= count - 3) return [1, 'ellipsis', count - 4, count - 3, count - 2, count - 1, count];
  return [1, 'ellipsis', current - 1, current, current + 1, 'ellipsis', count];
};

const languageLabel = (language: ArticleDraftListItem['language'] | ArticleDraft['language']) =>
  language === 'ar' ? 'العربية' : language === 'en' ? 'English' : language === 'mixed' ? 'عربي + English' : 'غير محدد';

const translationTargetLabel = (language: ArticleDraftListItem['language'] | ArticleDraft['language']) =>
  language === 'ar' ? 'English' : language === 'en' ? 'العربية' : 'لغة أخرى';

const publishedVersionLabel = (version?: ArticleDraft['publishedVersion']) =>
  version === 'translation' ? 'ترجمة AI' : version === 'ai' ? 'تعديل AI' : version === 'manual' ? 'تعديل يدوي' : 'الأصلية';

const NewsJudgments: React.FC = () => {
  const [drafts, setDrafts] = useState<ArticleDraftListItem[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [sourceName, setSourceName] = useState('');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selected, setSelected] = useState<ArticleDraft | null>(null);
  const [manualTitle, setManualTitle] = useState('');
  const [manualContent, setManualContent] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [translatingId, setTranslatingId] = useState<number | null>(null);
  const editorRef = useRef<RichTextEditorRef>(null);
  const limit = 20;

  const fetchSources = async () => {
    try {
      const response = await axiosInstance.get(api.drafts.sources);
      setSources(response.data.sources || []);
    } catch {
      // ponytail: filter still works by typing if sources fail
    }
  };

  const fetchDrafts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(
        api.drafts.getAll({
          sourceName: sourceName || undefined,
          page,
          limit,
          q: search || undefined,
          status: status || undefined,
        })
      );
      setDrafts(response.data.drafts || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحميل الأخبار والأحكام');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    fetchDrafts();
  }, [page, sourceName, search, status]);

  const openDraft = async (id: number) => {
    try {
      setError('');
      const response = await axiosInstance.get(api.drafts.getById(id));
      const draft: ArticleDraft = response.data;
      setSelected(draft);
      setManualTitle(draft.editedTitle || draft.title);
      setManualContent(draft.manualContent || draft.aiContent || draft.translationContent || draft.originalContent || '');
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل فتح المسودة');
    }
  };

  const closeDraft = () => {
    setSelected(null);
    setManualTitle('');
    setManualContent('');
    setActionLoading('');
  };

  const handleDeleteDraft = async (draft: Pick<ArticleDraftListItem, 'id' | 'status' | 'parentDraftId' | 'childDraftCount'>) => {
    const hasChildren = !draft.parentDraftId && Boolean(draft.childDraftCount);
    const publishedWarning = draft.status === 'published'
      ? '\n\nالمقال المنشور سيبقى ظاهرًا في المدونة.'
      : '';
    const childrenWarning = hasChildren
      ? `\n\nالمسودة الأصلية لها ${draft.childDraftCount} نسخ تابعة. احذف النسخ التابعة أولاً.`
      : '';
    if (!window.confirm(`حذف هذه المسودة نهائيًا؟ لا يمكن التراجع عن الحذف.${childrenWarning}${publishedWarning}`)) return;

    try {
      setDeletingId(draft.id);
      setError('');
      setSuccess('');
      await axiosInstance.delete(api.drafts.delete(draft.id));
      setSuccess('تم حذف المسودة بنجاح');
      if (selected?.id === draft.id) closeDraft();
      if (drafts.length === 1 && page > 1) setPage((current) => current - 1);
      else await fetchDrafts();
      fetchSources();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل حذف المسودة');
    } finally {
      setDeletingId(null);
    }
  };
  const runAction = async (key: string, fn: () => Promise<void>, okMessage: string | (() => string)) => {
    try {
      setActionLoading(key);
      setError('');
      setSuccess('');
      await fn();
      setSuccess(typeof okMessage === 'function' ? okMessage() : okMessage);
      fetchDrafts();
      fetchSources();
    } catch (err: any) {
      setError(err.response?.data?.error || 'حدث خطأ أثناء تنفيذ العملية');
    } finally {
      setActionLoading('');
    }
  };

  const handleSaveManual = () => {
    if (!selected) return;
    const content = editorRef.current?.getValue() || manualContent;
    runAction(
      'manual',
      async () => {
        const response = await axiosInstance.put(api.drafts.saveManual(selected.id), {
          title: manualTitle,
          content,
        });
        setSelected(response.data);
        setManualContent(response.data.manualContent || content);
      },
      'تم حفظ التعديل اليدوي. الأصلي لم يتغير.'
    );
  };

  const handleAiRewrite = () => {
    if (!selected) return;
    let createdMessage = 'تم إنشاء النسخة المعدّلة بالذكاء الاصطناعي';
    runAction(
      'ai',
      async () => {
        const response = await axiosInstance.post(api.drafts.aiRewrite(selected.id), {}, { timeout: 120000 });
        setSelected(response.data);
        setManualTitle(response.data.editedTitle || response.data.title);
        setManualContent(response.data.aiContent || response.data.originalContent || '');
        createdMessage = `تم إنشاء المسودة #${response.data.id} كنسخة AI تابعة للأصل #${response.data.rootDraftId || response.data.parentDraftId}`;
      },
      () => createdMessage
    );
  };

  const handleTranslateDraft = async (draft: ArticleDraftListItem | ArticleDraft) => {
    if (draft.isBilingual || (draft.language !== 'ar' && draft.language !== 'en')) return;
    const targetLanguage = draft.language === 'ar' ? 'en' : 'ar';
    if (!window.confirm(`ترجمة المسودة إلى ${translationTargetLabel(draft.language)} باستخدام Google Gemini؟ سيتم إنشاء مسودة تابعة للأصل.`)) return;

    try {
      setTranslatingId(draft.id);
      setError('');
      setSuccess('');
      const response = await axiosInstance.post(api.drafts.translate(draft.id), {}, { timeout: 120000 });
      const translatedDraft: ArticleDraft = response.data;
      setSelected(translatedDraft);
      setManualTitle(translatedDraft.editedTitle || translatedDraft.title);
      setManualContent(translatedDraft.translationContent || translatedDraft.originalContent || '');
      setSuccess(`تم إنشاء المسودة ${languageLabel(targetLanguage)} #${translatedDraft.id}، التابعة للأصل #${translatedDraft.rootDraftId || translatedDraft.parentDraftId}`);
      await fetchDrafts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشلت ترجمة المسودة');
    } finally {
      setTranslatingId(null);
    }
  };

  const handlePublishOriginal = () => {
    if (!selected) return;
    if (!window.confirm('نشر النسخة الأصلية إلى المدونة؟ سيتم توضيح اللغة التي نُشرت.')) return;
    let publishedMessage = 'تم نشر النسخة الأصلية';
    runAction(
      'publish-original',
      async () => {
        const response = await axiosInstance.post(api.drafts.publishOriginal(selected.id));
        setSelected(response.data.draft);
        const details = response.data.publishedDetails;
        publishedMessage = `تم نشر الأصلية #${details?.draftId || selected.rootDraftId || selected.id} (${(details?.languages || []).map(languageLabel).join(' + ')})`;
      },
      () => publishedMessage
    );
  };

  const handlePublishEdited = () => {
    if (!selected) return;
    if (!window.confirm(`نشر ${selected.editType === 'translation' ? 'الترجمة' : 'النسخة المعدّلة'} إلى المدونة؟`)) return;
    let publishedMessage = 'تم نشر النسخة المعدّلة';
    runAction(
      'publish-edited',
      async () => {
        const response = await axiosInstance.post(api.drafts.publishEdited(selected.id));
        setSelected(response.data.draft);
        const details = response.data.publishedDetails;
        publishedMessage = `تم نشر ${publishedVersionLabel(details?.version)} للمسودة #${details?.draftId || selected.id} (${(details?.languages || []).map(languageLabel).join(' + ')})`;
      },
      () => publishedMessage
    );
  };

  const hasEdited = Boolean(selected?.manualContent || selected?.aiContent || selected?.translationContent);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-paper)' }}>
      <nav
        style={{
          backgroundColor: '#041525',
          borderBottom: '2px solid #477CD7',
          width: '100%',
          height: '72px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full relative">
            <AdminBackLink />
            <h1
              className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2"
              style={{ fontFamily: 'var(--font-arabtype)', letterSpacing: '0.5px' }}
            >
              الأخبار والأحكام
            </h1>
            <div className="w-24" />
          </div>
        </div>
      </nav>

      <div className="p-8 news-page">
        <div className="max-w-7xl mx-auto">
          <section className="news-overview">
            <div className="news-heading-row">
              <div>
                <span className="news-eyebrow">المحتوى التحريري</span>
                <h2>الأخبار والأحكام</h2>
                <p>راجع المواد الواردة، عدّلها، ثم انشر النسخة المناسبة على المدونة.</p>
              </div>
              <div className="news-total-card">
                <span>نتائج البحث</span>
                <strong>{total}</strong>
                <small>مادة</small>
              </div>
            </div>

            <div className="news-filter-panel">
              <label className="news-search-field">
                <span>بحث بالعنوان</span>
                <input
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="اكتب جزءًا من عنوان الخبر أو الحكم"
                />
              </label>
              <label className="news-select-field">
                <span>المصدر</span>
                <select value={sourceName} onChange={(event) => { setPage(1); setSourceName(event.target.value); }}>
                  <option value="">كل المواقع</option>
                  {sources.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </label>
              <label className="news-select-field">
                <span>حالة النشر</span>
                <select value={status} onChange={(event) => { setPage(1); setStatus(event.target.value); }}>
                  <option value="">كل الحالات</option>
                  <option value="draft">مسودات</option>
                  <option value="published">منشورة</option>
                </select>
              </label>
              {(searchInput || sourceName || status) && (
                <button
                  type="button"
                  className="news-reset-button"
                  onClick={() => { setSearchInput(''); setSearch(''); setSourceName(''); setStatus(''); setPage(1); }}
                >
                  مسح الفلاتر
                </button>
              )}
            </div>

            {error && <div className="news-alert news-alert-error" role="alert">{error}</div>}
            {success && <div className="news-alert news-alert-success" role="status">{success}</div>}

            <div className="news-list-card">
              <div className="news-list-header">
                <div>
                  <h3>قائمة المواد</h3>
                  <p>{total ? `عرض ${Math.min((page - 1) * limit + 1, total)}–${Math.min(page * limit, total)} من ${total}` : 'لا توجد نتائج'}</p>
                </div>
                <span className="news-page-indicator">صفحة {page} من {totalPages}</span>
              </div>

              {loading ? (
                <div className="news-empty-state"><span className="news-loader" />جاري تحميل المواد...</div>
              ) : drafts.length === 0 ? (
                <div className="news-empty-state">
                  <strong>{search || sourceName || status ? 'لا توجد نتائج مطابقة' : 'لا توجد مواد حتى الآن'}</strong>
                  <span>{search || sourceName || status ? 'جرّب تغيير البحث أو الفلاتر.' : 'ستظهر الأخبار والأحكام الواردة هنا.'}</span>
                </div>
              ) : (
                <div className="news-table-scroll">
                  <table className="news-table">
                    <thead>
                      <tr>
                        <th>المادة</th>
                        <th>رقم المسودة</th>
                        <th>الأصل</th>
                        <th>المصدر</th>
                        <th>تاريخ الورود</th>
                        <th>الحالة</th>
                        <th>النسخة</th>
                        <th className="news-actions-heading">الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drafts.map((draft) => {
                        const thumb = getImageUrl(draft.imageUrl);
                        return (
                          <tr key={draft.id} onClick={() => openDraft(draft.id)}>
                            <td>
                              <div className="news-article-cell">
                                {thumb ? <img src={thumb} alt="" className="news-thumb" /> : <span className="news-thumb news-thumb-empty">KIG</span>}
                                <div className="news-article-copy">
                                  <strong>{draft.editedTitle || draft.title}</strong>
                                  <div className="news-draft-badges">
                                    <span className={`news-language-chip lang-${draft.language}`}>{languageLabel(draft.language)}</span>
                                    {draft.isBilingual && <span className="news-parent-chip is-bilingual">نسخة عربية وإنجليزية متاحة</span>}
                                  </div>
                                  {draft.originalUrl && <span>{draft.originalUrl.replace(/^https?:\/\//, '').split('/')[0]}</span>}
                                </div>
                              </div>
                            </td>
                            <td><span className="news-draft-id">#{draft.id}</span></td>
                            <td>
                              {draft.parentDraftId
                                ? <span className="news-parent-chip">تابعة للأصل #{draft.parentDraftId}</span>
                                : <span className="news-parent-chip is-root">نسخة أصلية</span>}
                            </td>
                            <td><span className="news-source-chip">{draft.sourceName || 'غير محدد'}</span></td>
                            <td className="news-date-cell">{formatDate(draft.sourcePublishedAt || draft.createdAt)}</td>
                            <td>
                              <span className={`news-status ${draft.status === 'published' ? 'is-published' : 'is-draft'}`}>
                                <i />{draft.status === 'published' ? 'منشور' : 'مسودة'}
                              </span>
                            </td>
                            <td>
                              {draft.status === 'published'
                                ? <span className={`news-edit-chip edit-${draft.publishedVersion || 'original'}`}>نُشر {languageLabel(draft.language)} · {publishedVersionLabel(draft.publishedVersion)}</span>
                                : draft.editType === 'none'
                                  ? <span className="news-muted">بدون تعديل</span>
                                  : <span className={`news-edit-chip edit-${draft.editType}`}>{draft.editType === 'ai' ? 'معدّل بالذكاء الاصطناعي' : draft.editType === 'translation' ? 'ترجمة AI' : 'تعديل يدوي'}</span>}
                            </td>
                            <td>
                              <div className="news-row-actions" onClick={(event) => event.stopPropagation()}>
                                <button
                                  type="button"
                                  className="news-translate-button"
                                  onClick={() => handleTranslateDraft(draft)}
                                  disabled={Boolean(actionLoading) || translatingId !== null || draft.isBilingual || (draft.language !== 'ar' && draft.language !== 'en')}
                                  title={draft.isBilingual ? 'توجد بالفعل نسخة عربية وإنجليزية' : `ترجمة إلى ${translationTargetLabel(draft.language)}`}
                                >
                                  {translatingId === draft.id ? 'جاري الترجمة…' : 'ترجمة AI'}
                                </button>
                                <button type="button" className="news-icon-button" onClick={() => openDraft(draft.id)} aria-label="فتح المادة" title="فتح المادة">
                                  {React.createElement(HiOutlineEye as any, { "aria-hidden": true })}
                                </button>
                                <button
                                  type="button"
                                  className="news-icon-button news-delete-button"
                                  onClick={() => handleDeleteDraft(draft)}
                                  disabled={deletingId === draft.id}
                                  aria-label={`حذف ${draft.title}`}
                                  title="حذف المادة"
                                >
                                  {React.createElement(HiOutlineTrash as any, { "aria-hidden": true })}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <nav className="news-pagination" aria-label="التنقل بين صفحات الأخبار والأحكام">
                  <button type="button" className="news-page-button news-page-arrow" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>السابق</button>
                  <div className="news-page-numbers">
                    {getPaginationItems(page, totalPages).map((item, index) => item === 'ellipsis'
                      ? <span key={`gap-${index}`} className="news-page-gap" aria-hidden="true">…</span>
                      : <button key={item} type="button" className={`news-page-button ${page === item ? 'is-current' : ''}`} aria-current={page === item ? 'page' : undefined} onClick={() => setPage(item)} disabled={loading}>{item}</button>)}
                  </div>
                  <button type="button" className="news-page-button news-page-arrow" disabled={page >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>التالي</button>
                </nav>
              )}
            </div>
          </section>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[92vh] overflow-y-auto">
            <div className="p-6 space-y-5">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selected.editedTitle || selected.title}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {selected.sourceName} · {formatDate(selected.sourcePublishedAt)} · المسودة #{selected.id}
                    {selected.blogPostId ? ` · منشور في المدونة #${selected.blogPostId}` : ''}
                  </p>
                  <div className="news-draft-badges news-modal-badges">
                    <span className={`news-language-chip lang-${selected.language}`}>{languageLabel(selected.language)}</span>
                    {selected.parentDraftId
                      ? <span className="news-parent-chip">مسودة تابعة للأصل #{selected.rootDraftId || selected.parentDraftId}</span>
                      : <span className="news-parent-chip is-root">المسودة الأصلية #{selected.id}</span>}
                    {selected.isBilingual && <span className="news-parent-chip is-bilingual">نسخة عربية وإنجليزية متاحة</span>}
                    {selected.status === 'published' && <span className="news-edit-chip">نُشرت: {publishedVersionLabel(selected.publishedVersion)} · {languageLabel(selected.language)}</span>}
                  </div>
                  {selected.originalUrl && (
                    <a
                      href={selected.originalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm"
                      style={{ color: '#477CD7' }}
                    >
                      الرابط الأصلي
                    </a>
                  )}
                </div>
                <button type="button" onClick={closeDraft} className="text-gray-400 hover:text-gray-700 text-2xl">
                  ×
                </button>
              </div>

              {(selected.images?.length || selected.imageUrl) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {(selected.images?.length
                    ? selected.images
                    : [{ id: 0, imageUrl: selected.imageUrl || '', orderIndex: 1, articleDraftId: selected.id }]
                  ).map((img) => {
                    const src = getImageUrl(img.imageUrl);
                    if (!src) return null;
                    return (
                      <img
                        key={img.id || img.imageUrl}
                        src={src}
                        alt=""
                        className="w-full h-32 object-cover rounded"
                      />
                    );
                  })}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={Boolean(actionLoading) || translatingId !== null || Boolean(selected.isBilingual) || (selected.language !== 'ar' && selected.language !== 'en')}
                  onClick={() => handleTranslateDraft(selected)}
                  className="news-translate-button news-translate-button-large"
                  title={selected.isBilingual ? 'توجد بالفعل نسخة عربية وإنجليزية' : `ترجمة إلى ${translationTargetLabel(selected.language)}`}
                >
                  {translatingId === selected.id ? 'جاري الترجمة…' : `ترجمة إلى ${translationTargetLabel(selected.language)} بالـ AI`}
                </button>
                <button
                  type="button"
                  disabled={Boolean(actionLoading)}
                  onClick={handlePublishOriginal}
                  className="px-4 py-2 rounded-lg text-white disabled:opacity-50"
                  style={{ background: 'var(--button-gradient)' }}
                >
                  {actionLoading === 'publish-original' ? 'جاري النشر...' : 'نشر النسخة الأصلية'}
                </button>
                <button
                  type="button"
                  disabled={Boolean(actionLoading)}
                  onClick={handleSaveManual}
                  className="px-4 py-2 rounded-lg border disabled:opacity-50"
                >
                  {actionLoading === 'manual' ? 'جاري الحفظ...' : 'حفظ التعديل اليدوي'}
                </button>
                <button
                  type="button"
                  disabled={Boolean(actionLoading)}
                  onClick={handleAiRewrite}
                  className="px-4 py-2 rounded-lg border disabled:opacity-50"
                >
                  {actionLoading === 'ai' ? 'جاري التعديل بالـ AI...' : 'تعديل بالـ AI'}
                </button>
                <button
                  type="button"
                  disabled={Boolean(actionLoading) || !hasEdited}
                  onClick={handlePublishEdited}
                  className="px-4 py-2 rounded-lg text-white disabled:opacity-40"
                  style={{ background: hasEdited ? '#082A49' : '#9ca3af' }}
                >
                  {actionLoading === 'publish-edited' ? 'جاري النشر...' : selected.editType === 'translation' ? 'نشر الترجمة' : 'نشر النسخة المعدّلة'}
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">العنوان (للتعديل اليدوي)</label>
                <input
                  value={manualTitle}
                  onChange={(e) => setManualTitle(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">المحتوى الأصلي</h3>
                <div
                  className="prose max-w-none border rounded-lg p-4 bg-gray-50 overflow-auto max-h-64"
                  dangerouslySetInnerHTML={{ __html: selected.originalContent || '' }}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">التعديل اليدوي</h3>
                <RichTextEditor
                  ref={editorRef}
                  value={manualContent}
                  onChange={setManualContent}
                  placeholder="عدّل المقال هنا. الأصلي يبقى كما هو."
                  height="280px"
                  direction="rtl"
                />
              </div>

              {selected.aiContent && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">نسخة الذكاء الاصطناعي</h3>
                  <div
                    className="prose max-w-none border rounded-lg p-4 bg-blue-50 overflow-auto max-h-64"
                    dangerouslySetInnerHTML={{ __html: selected.aiContent }}
                  />
                </div>
              )}

              {selected.translationContent && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">الترجمة إلى {languageLabel(selected.language)}</h3>
                  <div
                    className="prose max-w-none border rounded-lg p-4 bg-blue-50 overflow-auto max-h-64"
                    dangerouslySetInnerHTML={{ __html: selected.translationContent }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsJudgments;









